import React from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, Button, Stack, FormHelperText
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddIcon from "@mui/icons-material/Add";
import { FieldRenderer } from "./FormGenerator";

const defaultValueFor = (type) => {
    if (type === "addMultiple") return [];
    if (type === "addSingle") return {};
    if (type === "number") return "";
    return "";
};

const makeEmptyItem = (fields) =>
    (fields || []).reduce((acc, f) => {
        acc[f.id] = defaultValueFor(f.type);
        return acc;
    }, {});

export default function AddMultipleInputTable({
                                                  field,
                                                  value,
                                                  onChange,
                                                  parentSection,
                                                  error,
                                                  helperText,
                                                  reportValidity,
                                              }) {
    const items = Array.isArray(value) ? value : [];
    const columns = field.fields || [];

    const update = (next) => onChange(field.id, next, parentSection);

    const updateItem = (idx, key, val) => {
        const next = items.map((it, i) => (i === idx ? { ...it, [key]: val } : it));
        update(next);
    };

    const addItem = () => update([...(items || []), makeEmptyItem(columns)]);

    const removeItem = (idx) => {
        if (items.length <= 1) return;
        update(items.filter((_, i) => i !== idx));
    };

    React.useEffect(() => {
        if (items.length === 0) {
            update([makeEmptyItem(columns)]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length]);

    const rows = items.length ? items : [makeEmptyItem(columns)];

    const hasActions = items.length > 1;

    return (
        <Stack spacing={1}>
            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    overflowX: "auto",
                }}
            >
                <Table size="small" sx={{ tableLayout: "fixed" }}>
                    <TableHead>
                        <TableRow
                            sx={{
                                bgcolor: (t) => t.palette.grey[50],
                                "& th": { fontWeight: 600, py: 1.25 },
                            }}
                        >
                            {columns.map((col) => (
                                <TableCell key={col.id}>{col.label || col.id}</TableCell>
                            ))}
                            {hasActions && (
                                <TableCell align="right" sx={{ width: 120 }} whiteSpace='nowrap'>
                                    Actions
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {rows.map((item, idx) => (
                            <TableRow
                                key={idx}
                                sx={{
                                    "&:nth-of-type(odd)": {
                                        bgcolor: (t) => t.palette.action.hover,
                                    },
                                    "& td": { py: 1 },
                                }}
                            >
                                {columns.map((subField) => (
                                    <TableCell key={`${subField.id}-${idx}`} sx={{ verticalAlign: "top" }}>
                                        <FieldRenderer
                                            field={subField}
                                            value={item?.[subField.id]}
                                            onChange={(childFieldId, newVal) => updateItem(idx, childFieldId, newVal)}
                                            index={undefined}
                                            parentSection={parentSection}
                                            childSection={field.id}
                                            reportValidity={reportValidity}
                                            keyPath={`${field.id}[${idx}].${subField.id}`}
                                        />
                                    </TableCell>
                                ))}
                                {hasActions && (
                                    <TableCell align="right" sx={{ verticalAlign: "top" }}>
                                        <IconButton
                                            aria-label="remove row"
                                            onClick={() => removeItem(idx)}
                                            size="small"
                                            color="error"
                                        >
                                            <CloseRoundedIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {field.addPrompt && (
                <Button
                    onClick={addItem}
                    startIcon={<AddIcon />}
                    variant="contained"
                    sx={{ alignSelf: "flex-end" }}
                >Add
                </Button>
            )}

            {Array.isArray(items) && items.length === 0 && error && (
                <FormHelperText error sx={{ mt: 0.5 }}>
                    {helperText}
                </FormHelperText>
            )}
        </Stack>
    );
}
